import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';
import * as Localization from 'expo-localization';
import { Platform } from 'react-native';
import { v4 as uuidv4 } from 'uuid';
import * as Constants from './Constants';

// SecureStore (Android Keystore-backed) caps values at ~2048 bytes, so only small,
// genuinely sensitive values go there. Everything else (basket JSON, cached lists, etc.)
// uses AsyncStorage, which has no such size limit.
const SECURE_KEYS = new Set([
  Constants.password,
  Constants.mobile,
  Constants.TOKEN,
]);

// Storage functions that are used by both Commons and ServerOperations
export const getFromAS = async key => {
  try {
    if (Platform.OS === 'web' || !SECURE_KEYS.has(key)) {
      return await AsyncStorage.getItem(key);
    }
    return await SecureStore.getItemAsync(key);
  } catch (error) {
    console.log(error);
  }
};

export const saveToAS = async (key, value) => {
  try {
    if (Platform.OS === 'web' || !SECURE_KEYS.has(key)) {
      await AsyncStorage.setItem(key, value);
      return;
    }
    await SecureStore.setItemAsync(key, value);
  } catch (error) {
    console.log(error);
  }
};

export const multiSaveToAS = async (pairs) => {
  try {
    if (Platform.OS === 'web') {
      await AsyncStorage.multiSet(pairs);
      return;
    }
    await Promise.all(pairs.map(([key, value]) =>
      SECURE_KEYS.has(key)
        ? SecureStore.setItemAsync(key, value ?? '')
        : AsyncStorage.setItem(key, value ?? '')
    ));
  } catch (error) {
    console.log(error);
  }
};

export const removeFromAS = async (key) => {
  try {
    if (Platform.OS === 'web' || !SECURE_KEYS.has(key)) {
      return await AsyncStorage.removeItem(key);
    }
    return await SecureStore.deleteItemAsync(key);
  } catch (error) {
    console.log(error);
  }
};

// Device ID functions
export const getDeviceID = async () => {
  if (Platform.OS === 'web') {
    let fetchUUID = await AsyncStorage.getItem('secure_deviceid');
    if (fetchUUID) {
      return fetchUUID.replace(/"/g, "");
    }
    let uuid = uuidv4().replace(/"/g, "");
    await AsyncStorage.setItem('secure_deviceid', JSON.stringify(uuid));
    return uuid;
  } else {
    let fetchUUID = await SecureStore.getItemAsync('secure_deviceid');
    if (fetchUUID) {
      return fetchUUID.replace(/"/g, "");
    }
    let uuid = uuidv4().replace(/"/g, "");
    await SecureStore.setItemAsync('secure_deviceid', JSON.stringify(uuid));
    return uuid;
  }
};

// Language function
export const language = async () => {
  const languageFromStorage = await getFromAS(Constants.language);
  const locales = Localization.getLocales();
  let locale =
    (locales && locales.length > 0 && locales[0].languageCode) || "en";

  if (!locale.startsWith("ar") && !locale.startsWith("en")) {
    locale = "en";
  }
  locale = languageFromStorage == null ? locale : languageFromStorage;
  return locale;
};

// User object function
export const getUserObject = async () => {
  // password and mobile are kept out of this plaintext batch read — they are
  // stored/retrieved via getFromAS/saveToAS, which route to encrypted SecureStore on native.
  const keys = [
    Constants.email,
    Constants.username,
    Constants.firstName,
    Constants.lastName,
    Constants.birthdate,
    Constants.gender,
    Constants.location,
    Constants.maritalStatus,
    Constants.numOfChildren,
    Constants.address,
    Constants.loggedIn,
    Constants.isAdmin,
    Constants.addressNote,
  ];

  const values = await Promise.all(keys.map(key => getFromAS(key)));

  const user = {};
  user.EMAIL = values[0];
  user.USERNAME = values[1];
  user.PASSWORD = await getFromAS(Constants.password);
  user.FIRSTNAME = values[2];
  user.LASTNAME = values[3];
  user.BIRTHDATE = values[4];
  user.GENDER = values[5];
  user.MOBILE = await getFromAS(Constants.mobile);
  user.LOCATION = values[6];
  user.MARITAL_STATUS = values[7];
  user.NUM_OF_CHILDREN = values[8];
  user.ADDRESS = values[9];
  user.LOGGEDIN = values[10];
  user.ISADMIN = values[11];
  user.ADDRESS_NOTE = values[12];

  // One-time cleanup: scrub any plaintext password/mobile left behind in AsyncStorage
  // from before this data was migrated to encrypted SecureStore.
  if (Platform.OS !== 'web') {
    AsyncStorage.multiRemove([Constants.password, Constants.mobile]).catch(() => { });
  }

  const customerUSERNAME = await getFromAS("customerUSERNAME");
  const customerMOBILE = await getFromAS("customerMOBILE");
  if (
    customerUSERNAME != null &&
    customerUSERNAME != undefined &&
    customerUSERNAME != ""
  ) {
    user.USERNAME = customerUSERNAME;
    user.MOBILE = customerMOBILE;
  }

  if (user.USERNAME === "931386753963673") {
    user.MOBILE = "0797580004";
    user.ADDRESS = "Hidhab street no 6 Abdoun.. behind American embassy";
    user.LOCATION = "";
  }
  if (user.USERNAME === "519857305549339") {
    user.mobile = "0777707747";
  }
  if (user.USERNAME == "samarh") {
    user.LOCATION = "32.02800750732422,35.8565673828125";
  }
  return user;
};

// Push notifications token function (if needed)
export const getPushNotificationsToken = async () => {
  // This function might need to be implemented based on your notification setup
  return await getFromAS(Constants.PUSH_TOKEN);
};